import { FetchAssetAnalyticsResponse, ReplaceSimilarAssetResponse, UpdateAssetErrorResponse, UpdateAssetResponse } from "../../types/assets";
import { AddAssetToGroup, AddAssetToGroupFailed, AddAssetToGroupSucceeded, CreateGroup, CreateGroupFailed, CreateGroupSucceeded, DeleteGroupRequest, DeleteGroupSucceeded, FetchGroupChildrenFailed, FetchGroupChildrenSucceeded, FetchGroupFailed, FetchGroupSucceeded, UpdateGroupFailed, UpdateGroupSucceeded } from "../../types/groups";
import { ADD_ASSET_TO_GROUP, ADD_ASSET_TO_GROUP_FAILED, ADD_ASSET_TO_GROUP_SUCCEEDED, CREATING_GROUP_IN_GROUP, CREATING_GROUP_IN_GROUP_FAILED, CREATING_GROUP_IN_GROUP_SUCCEEDED, DELETING_ASSET_FROM_GROUP_FAILED, DELETING_ASSET_FROM_GROUP_REQUEST, DELETING_ASSET_FROM_GROUP_SUCCEEDED, DELETING_GROUP_FAILED, DELETING_GROUP_REQUEST, DELETING_GROUP_SUCCEEDED, FETCHING_ASSET_ANALYTICS_SUCCEEDED, FETCHING_GROUP_CHILDREN_FAILED, FETCHING_GROUP_CHILDREN_REQUEST, FETCHING_GROUP_CHILDREN_SUCCEEDED, FETCHING_GROUP_FAILED, FETCHING_GROUP_REQUEST, FETCHING_GROUP_SUCCEEDED, GROUP_UPDATE_PROPERTY, INVALIDATE_GROUP_CHILDREN, INVALIDATE_GROUP_CHILDREN_FETCHED, MOVING_ENTITY_SUCCEEDED, REPLACE_SIMILAR_ASSET_SUCCEEDED, SORTING_GROUP, SORTING_STREAM, UPDATING_ASSET_FAILED, UPDATING_ASSET_REQUEST, UPDATING_ASSET_SUCCEEDED, UPDATING_GROUP_FAILED, UPDATING_GROUP_REQUEST, UPDATING_GROUP_SUCCEEDED } from "../actions/actionTypes";
import {Group as GroupType} from "../../types/groups";
import _ from 'lodash';
import { getStreamSortPreference } from "src/utils/stream.helper";

interface Group {
  [id: string]: {
    loading: boolean;
    invalidated: boolean;
    loadingChildren: boolean;
    fetchedChildren: boolean;
    item: GroupType | null;
    groupCreation: {
      creating: boolean;
      created: boolean;
      item: GroupType | null;
    }
    creatingAsset: boolean;
    createdAsset: boolean;
    updation: GroupUpdationState;
    updatingAsset: boolean;
    errors: null;
    assetsDeletion: AssetsDeletion;
    groupsDeletion: GroupsDeletion;
    pagination: {
      currentPage: number;
      totalPages: number;
      hasMoreItems: boolean;
    },
    sort: {
      type: string;
      order: string;
    },
    edit: GroupEditState | null;
  }
}

interface GroupUpdationState {
  updating: boolean;
  updated: boolean;
  error?: any;
}

interface GroupEditState {
  [property: string]: any;
}

interface AssetsDeletion {
  deleting: boolean,
  deleted: boolean
}

interface GroupsDeletion {
  deleting: boolean,
  deleted: boolean
}

const initialGroupUpdationState: GroupUpdationState = {
  updating: false,
  updated: false,
  error: null
}

const initialAssetsDeletion: AssetsDeletion = {
  deleting: false,
  deleted: false
}

const initialGroupsDeletion: GroupsDeletion = {
  deleting: false,
  deleted: false
}

const initialState: Group = {
  id: {
    loading: false,
    invalidated: true,
    item: null,
    groupCreation: {
      creating: false,
      created: false,
      item: null
    },
    creatingAsset: false,
    createdAsset: false,
    updation: initialGroupUpdationState,
    updatingAsset: false,
    loadingChildren: false,
    fetchedChildren: false,
    errors: null,
    assetsDeletion: initialAssetsDeletion,
    groupsDeletion: initialGroupsDeletion,
    pagination: {
      currentPage: 0,
      totalPages: 0,
      hasMoreItems: false
    },
    sort: {
      type: "name",
      order: "ASC",
    },
    edit: null
  } 
};

interface GroupAction {
  [key: string]: any;
}

type Action = GroupAction;

function sortChildren(type: string, order: string, items: any[]) {
  if (!items || items.length === 0) {
    return [];
  }
  const sortedGroup = items.filter((item) => {
    return item.entityType === 'group';
  });

  const sortedAsset = items.filter((item) => {
    return item.entityType === 'asset';
  });

  const compareByName = (a: any, b: any) => {
    const nameA = a.name ? a.name.toLowerCase() : '';
    const nameB = b.name ? b.name.toLowerCase() : '';

    const numA = parseFloat(nameA);
    const numB = parseFloat(nameB);

    if (!isNaN(numA) && !isNaN(numB)) {
      return order === "ASC" ? numA - numB : numB - numA;
    } else {
      return order === "ASC" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
  };

  if (type === "name") {
    sortedGroup.sort(compareByName);
  }

  if (type === 'created') {
    sortedGroup.sort((a: any, b: any) => {
      const nameA = ((a.createdBy?.firstName ?? '') + ' ' + (a.createdBy?.lastName ?? '')).trim().toLowerCase();
      const nameB = ((b.createdBy?.firstName ?? '') + ' ' + (b.createdBy?.lastName ?? '')).trim().toLowerCase();
      return order === 'ASC'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    sortedAsset.sort((a: any, b: any) => {
      const nameA = ((a.author?.firstName ?? '') + ' ' + (a.author?.lastName ?? '')).trim().toLowerCase();
      const nameB = ((b.author?.firstName ?? '') + ' ' + (b.author?.lastName ?? '')).trim().toLowerCase();
      return order === 'ASC'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }

  else if (type === 'modified_date') {
    sortedGroup.sort((a: any, b: any) => {
      const aTime = new Date(a.modifiedDate).getTime();
      const bTime = new Date(b.modifiedDate).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });

    sortedAsset.sort((a: any, b: any) => {
      const aTime = new Date(a.modifiedDate).getTime();
      const bTime = new Date(b.modifiedDate).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });
  }

  if (type !== "name") {
    sortedAsset.sort((a: any, b: any) => {
      const analyticsA = (a.analytics) || {};
      const analyticsB = (b.analytics) || {};

      let valueA = 0, valueB = 0;

      if (type === "views") {
        valueA = analyticsA.views || 0;
        valueB = analyticsB.views || 0;
      } else if (type === "shares") {
        valueA = analyticsA.shares || 0;
        valueB = analyticsB.shares || 0;
      } else if (type === "downloads") {
        valueA = analyticsA.downloads || 0;
        valueB = analyticsB.downloads || 0;
      } else if (type === "timeSpent") {
        valueA = analyticsA.avgTimeSpent || 0;
        valueB = analyticsB.avgTimeSpent || 0;
      }

      if (order === "ASC") {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  } else {
    sortedAsset.sort(compareByName);
  }

  return [...sortedGroup, ...sortedAsset];
}


export function groupsReducer(state: Group = initialState, action: Action): Group {
  switch (action.type) {

    case FETCHING_GROUP_REQUEST: {
      const id = action.payload.id;
      const group = state[id] || initialState;
      return {
        ...state,
        [id]: {
          ...group,
          loading: true,
          invalidated: false,
          errors: null
        }
      };
    }

    case FETCHING_GROUP_SUCCEEDED: {

      const { group, id } = action.payload as FetchGroupSucceeded;
      const groupState = state[id] || initialState;
      return {
        ...state,
        [id]: {
          ...groupState,
          loading: false,
          item: group,
          errors: null
        }
      } as Group;

    }

    case FETCHING_GROUP_FAILED: {

      const { error, id } = action.payload as FetchGroupFailed;
      const group = state[id] || initialState;
      return {
        ...state,
        [id]: {
          ...group,
          loading: false,
          errors: error
        }
      };

    }

    case FETCHING_GROUP_CHILDREN_REQUEST: {

      const { id } = action.payload;
      const group = state[id] || initialState;
      return {
        ...state,
        [id]: {
          ...group,
          loadingChildren: true,
          errors: null
        }
      };
    }

    case FETCHING_GROUP_CHILDREN_SUCCEEDED: {

      let { entities, groupId, currentPage, totalPages } = action.payload as FetchGroupChildrenSucceeded;
      const group = state[groupId] || initialState;

      if(currentPage !== 0){
        entities = [...(group.item?.children || []), ...entities];
      }

      const sortPreference = getStreamSortPreference();

      const sortedChildren = sortPreference &&
      sortPreference.sortBy !== 'name' &&
      sortPreference.sortBy !== 'modified_date'
      ? sortChildren(sortPreference.sortBy, sortPreference.orderBy, entities)
      : entities;


      return {
        ...state,
        [groupId]: {
          ...group,
          loadingChildren: false,
          fetchedChildren: true,
          item: {
            ...group.item,
            children: sortedChildren,
          },
          pagination: {
            currentPage,
            totalPages,
            hasMoreItems: currentPage < totalPages - 1
          },
          errors: null
        }
      } as Group;
    }

    case FETCHING_GROUP_CHILDREN_FAILED: {

      const { error, groupId } = action.payload as FetchGroupChildrenFailed;
      const group = state[groupId] || initialState;
      return {
        ...state,
        [groupId]: {
          ...group,
          loadingChildren: false,
          fetchedChildren: true,
          errors: error
        }
      }
    }

    case INVALIDATE_GROUP_CHILDREN: {
      const groupId = action.payload;
      const group = state[groupId];
    
      if (!group) return state;
    
      return {
        ...state,
        [groupId]: {
          ...group,
          item: {
            ...group.item,
            children: [],
          },
          fetchedChildren: false,
          loadingChildren: false,
        }
      };
    }
    

    case UPDATING_ASSET_REQUEST: {

      const data = action.payload;
      const updatedAssetId = data.request.id;

      if (!data.parent || !state[data.parent]) {
        return state;
      }

      const updatedChildren = state[data.parent]?.item?.children?.map((child: any) =>
        child.id === updatedAssetId ? { ...child, name: data.name } : child
      );

      return {
        ...state,
        [data.parent]: {
          ...state[data.parent],
          updatingAsset: true,
          item: {
            ...state[data.parent].item,
            children: updatedChildren
          }
        }
      }
    }

    case UPDATING_ASSET_SUCCEEDED: {

      const data = action.payload as UpdateAssetResponse;
      const updatedAssetId = data.response.id;

      if (!data.parent || !state[data.parent]) {
        return state;
      }

      const updatedChildren = state[data.parent]?.item?.children?.map((child: any) =>
        child.id === updatedAssetId ? { ...child, ...data.response } : child
      );

      return {
        ...state,
        [data.parent]: {
          ...state[data.parent],
          updatingAsset: false,
          item: {
            ...state[data.parent].item,
            children: updatedChildren
          }
        }
      } as Group;
    }

    case UPDATING_ASSET_FAILED: {
      const data = action.payload as UpdateAssetErrorResponse;

      if (!data.parent || !state[data.parent]) {
        return state;
      }

      return {
        ...state,
        [data.parent]: {
          ...state[data.parent],
          updatingAsset: false,
          errors: data.error
        }
      }
    }

    case UPDATING_GROUP_REQUEST: {
      const data = action.payload;
      const updatedAssetId = data.entity.id;


      if (!state[data.parent]) {
        return state;
      }

      const updatedChildren = state[data.parent]?.item?.children?.map((child: any) =>
        child.id === updatedAssetId ? { ...child, name: data.entity.name } : child
      );

      return {
        ...state,
        [data.parent]: {
          ...state[data.parent],
          updation: {
            updating: true,
            updated: false
          },
          item: {
            ...state[data.parent].item,
            children: updatedChildren
          }
        }
      }
    }

    case UPDATING_GROUP_SUCCEEDED: {
      const data = action.payload as UpdateGroupSucceeded;
      const updatedGroupId = data.group.id;


      if (!state[data.parentId]) {
        return state;
      }

      const updatedChildren = state[data.parentId]?.item?.children?.map((child: any) =>
        child.id === updatedGroupId ? { ...child, ...data.group } : child
      );

      return {
        ...state,
        [data.parentId]: {
          ...state[data.parentId],
          updation: {
            updating: false,
            updated: true
          },
          item: {
            ...state[data.parentId].item,
            ...data.group,
            children: updatedChildren
          }
        }
      } as Group;
    }

    case UPDATING_GROUP_FAILED: {
      const data = action.payload as UpdateGroupFailed;

      if (!state[data.parentId]) {
        return state;
      }

      return {
        ...state,
        [data.parentId]: {
          ...state[data.parentId],
          updation: {
            updating: false,
            updated: false,
            error: data.error
          }
        }
      }
    }

    case DELETING_ASSET_FROM_GROUP_REQUEST: {
      const data = action.payload;
      const groupId = data.groupId;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          assetsDeletion: {
            deleting: true,
            deleted: false
          },
        }
      }
    }

    case DELETING_ASSET_FROM_GROUP_SUCCEEDED: {
      const data = action.payload;
      const assets = data.response;
      const groupId = data.groupId;

      const assetIds = assets.map((asset: any) => asset.id);
      const updatedChildren = state[groupId]?.item?.children?.filter((child: any) => !assetIds.includes(child.id));

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          assetsDeletion: {
            deleting: false,
            deleted: true
          },
          item: {
            ...state[groupId].item,
            children: updatedChildren
          }
        }
      }
    }

    case DELETING_ASSET_FROM_GROUP_FAILED: {
      const data = action.payload;
      const groupId = data.groupId;
      const error = action.payload;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          item: {
            ...state[groupId].item,
            assetsDeletion: {
              deleting: false,
              deleted: false
            },
            error: error
          }
        }
      }
    }

    case DELETING_GROUP_REQUEST: {

      const data = action.payload;
      const groupId = data.groupId;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          groupsDeletion: {
            deleting: true,
            deleted: false
          }
        }
      }
    }

    case DELETING_GROUP_SUCCEEDED: {

      const data = action.payload as DeleteGroupSucceeded;
      const group = data.group;
      const groupId = data.groupId;

      if(!state[groupId]) {
        return state;
      }

      const updatedChildren = state[groupId]?.item?.children?.filter((child: any) => child.id !== group);

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          groupsDeletion: {
            deleting: false,
            deleted: true
          },
          item: {
            ...state[groupId].item,
            children: updatedChildren
          }
        }
      } as Group;
    }

    case DELETING_GROUP_FAILED: {

      const data = action.payload as DeleteGroupRequest;
      const groupId = data.groupId;
      const error = action.payload;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          groupsDeletion: {
            deleting: false,
            deleted: false
          },
          item: {
            ...state[groupId].item,
          },
          errors: error
        }
      } as Group;
    }

    case FETCHING_ASSET_ANALYTICS_SUCCEEDED: {
      const data = action.payload as FetchAssetAnalyticsResponse;
      const assetId = data.assetId;
      const groupId = data.groupId || "";

      if (!state[groupId]) {
        return state;
      }

      const sortPreference = getStreamSortPreference();

      const updatedChildren = (state[groupId]?.item?.children ?? []).map(
        (child: any) =>
          child.id === assetId
            ? { ...child, analytics: data.response }
            : child
      );

      const sortedChildren = sortPreference &&
      sortPreference.sortBy !== 'name' &&
      sortPreference.sortBy !== 'modified_date'
      ? sortChildren(sortPreference.sortBy, sortPreference.orderBy, updatedChildren)
      : updatedChildren;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          // loading: false,
          item: {
            ...state[groupId].item,
            children: sortedChildren
          }
        }
      } as Group;
    }


    case SORTING_GROUP: {
      const data = action.payload;
     
      if(!state[data.id]) {
        return state;
      }

      const sortedChildren = sortChildren(data.sort.type, data.sort.order, state[data.id]?.item?.children ?? []);

      return {
        ...state,
        [data.id]: {
          ...state[data.id],
          item: {
            ...state[data.id].item,
            children: sortedChildren
          },
          sort: data.sort
        }
      }
    }
    
    case GROUP_UPDATE_PROPERTY:{
      const { id, property, value } = action.payload as { id: string, property: string, value: any }; 

      return {
        ...state,
        [id]: {
          ...state[id],
          edit: {
            [property]: value
          }
        }
      }
    }

    case MOVING_ENTITY_SUCCEEDED: {
      const data = action.payload;
      const groupId = data.source.id;
      const entities = data.entities;
      const entityIds = entities.map((entity: any) => entity.id);
      const updatedChildren = state[groupId]?.item?.children?.filter((child: any) => !entityIds.includes(child.id));

      if (!state[groupId]?.item) {
        return state;
      }

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          item: {
          ...state[groupId].item,
          children: updatedChildren
          }
        }
      }
    }

    case CREATING_GROUP_IN_GROUP: {
      const data = action.payload as CreateGroup;
      const groupId = data.groupId;
      const group = state[groupId] || initialState;
      return {
        ...state,
        [groupId]: {
          ...group,
          groupCreation: {
            creating: true,
            created: false,
            item: null
          },
        }
      }
    }

    case CREATING_GROUP_IN_GROUP_SUCCEEDED: {
      const { group, parentId } = action.payload as CreateGroupSucceeded;
      const groupState = state[parentId] || initialState;
      const updatedChildren = [...(groupState.item?.children || []), group];

      return {
        ...state,
        [parentId]: {
          ...groupState,
          groupCreation: {
            creating: false,
            created: true,
            item: group
          },
          item: {
            ...groupState.item,
            children: updatedChildren
          }
        }
      } as Group;
    }

    case CREATING_GROUP_IN_GROUP_FAILED: {
      const data = action.payload as CreateGroupFailed;
      const groupId = data.groupId;
      const group = state[groupId] || initialState;
      return {
        ...state,
        [groupId]: {
          ...group,
          groupCreation: {
            creating: false,
            created: false,
            item: null
          },
        }
      }
    }

    case ADD_ASSET_TO_GROUP: {

      const data = action.payload as AddAssetToGroup;
      const groupId = data.groupId;

      return {
        ...state,
        [groupId]: {
          ...state[groupId],
          creatingAsset: true,
          createdAsset: false
        }
      }
    }

    case ADD_ASSET_TO_GROUP_SUCCEEDED: {
      
      const data = action.payload as AddAssetToGroupSucceeded;

      let updatedChildren = [...(state[data.groupId]?.item?.children || []), ...data.assets];
      updatedChildren = _.uniqBy(updatedChildren, 'id');

      const sortType = state.sort ? state[data.groupId].sort.type : "Name";
      const sortOrder = state.sort ? state[data.groupId].sort.order : "ASC";

      updatedChildren = sortChildren(sortType, sortOrder, updatedChildren);

      return {
        ...state,
        [data.groupId]: {
          ...state[data.groupId],
          creatingAsset: false,
          createdAsset: true,
          item: {
            ...state[data.groupId].item,
            children: updatedChildren,
            assetsCount: (state[data.groupId].item?.assetsCount || 0) + data.assets.length
          }
        }
      } as Group;
    }

    case ADD_ASSET_TO_GROUP_FAILED: {

      const data = action.payload as AddAssetToGroupFailed;

      return {
        ...state,
        [data.groupId]: {
          ...state[data.groupId],
          creatingAsset: false,
          createdAsset: false,
          errors: data.error
        }
      }
    }
    case INVALIDATE_GROUP_CHILDREN_FETCHED: {
      const id = action.payload.id;
      const group = state[id] || initialState;
      return {
        ...state,
        [id]: {
          ...group,
          fetchedChildren: false,
        }
      }
    }

    case REPLACE_SIMILAR_ASSET_SUCCEEDED: {

      const response = action.payload as ReplaceSimilarAssetResponse;

      const newAssetId = response.newAssetId;
      const existingAsset = response.existingAsset;
      const groups = response.groups;

      if(_.size(groups) > 0){
        const updatedItems = { ...state };
    
        Object.keys(state).forEach((id) => {
          const group = state[id];
    
          if (!group || !group?.item?.children) {
            return;
          }
          
          const newAsset = _.find(group.item.children, (asset) => {
            return asset.id === newAssetId;
          }); 
    
          if (newAsset) {
            let assets = group.item.children.map((asset) => {
              if (asset.id === newAssetId) {
                return existingAsset;
                }
                return asset;
              });
    
              assets = _.uniqBy(assets, "id");
    
              updatedItems[id] = {
                ...group,
                item: {
                  ...group.item,
                  children: assets,
                  assetsCount: _.size(assets)
                },
              };
              }
            });
    
            return {
            ...state,
            ...updatedItems,
            };
          } 
    
          return {
            ...state
          }
      }

    default: {
      return state;
    }
  }
}